import React from 'react';

const PrivacyPolicy = (): React.ReactElement => {
    return (
        <div className="privacy-policy max-w-4xl mx-auto p-6 bg-white" style={{ fontFamily: 'Geist, sans-serif' }}>
            <h1 className="text-3xl font-bold text-green mb-4">Privacy Policy</h1>
            <p className="text-gray-600 mb-6"><strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

            <div className="mb-6">
                <p className="text-gray-700">
                    WILLOW LOGISTICS INC ("Company", "we", "us", or "our") is committed to protecting your privacy.
                    This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services.
                </p>
            </div>

            <h2 className="text-2xl font-semibold text-gray-800 mb-3">1. Compliance</h2>
            <p className="text-gray-700 mb-4">
                By using our services, you agree to the collection and use of information in accordance with this Privacy Policy.
                You may unsubscribe from marketing communications at any time by following the instructions in those communications.
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mb-3">2. Information We Collect</h2>
            <p className="text-gray-700 mb-4">We collect the following types of information:</p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2">Personal Information:</h3>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li>Name and contact information (email, phone number)</li>
                <li>Company information and business details</li>
                <li>Account credentials and preferences</li>
                <li>Communication preferences</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2">Technical Information:</h3>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
                <li>IP address and location data</li>
                <li>Browser type and version</li>
                <li>Device information</li>
                <li>Usage data and analytics</li>
            </ul>

            <p className="text-gray-700 mb-6">
                <strong>We do not collect sensitive personal information</strong> such as social security numbers, financial account numbers,
                or health information unless specifically required for our services.
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mb-3">3. How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">We use your information to:</p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                <li>Fulfill our contractual obligations and provide our logistics services</li>
                <li>Improve our business operations and user experience</li>
                <li>Enhance website functionality and performance</li>
                <li>Conduct diagnostic and administrative activities</li>
                <li>Communicate important service updates and notifications</li>
                <li>Respond to inquiries and provide customer support</li>
                <li>Serve our legitimate business interests</li>
                <li>Protect the safety and security of our users and services</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-800 mb-3">4. Information Sharing and Disclosure</h2>
            <p className="text-gray-700 mb-4">
                <strong>We do not share your personal information with third parties for marketing or promotional purposes.</strong>
            </p>
            <p className="text-gray-700 mb-4">We may share your information with:</p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                <li>Service providers and subcontractors who assist in delivering our services</li>
                <li>Business partners when necessary to fulfill our services to you</li>
                <li>Group companies within our corporate family</li>
                <li>Law enforcement or regulatory agencies when required by law</li>
                <li>Third parties with your explicit consent</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-800 mb-3">5. SMS/Text Messaging Services</h2>
            <p className="text-gray-700 mb-4">
                We use Twilio, a third-party service provider, to send SMS notifications and alerts.
                By providing your phone number and opting in to receive messages, you consent to receive informational messages and alerts from WILLOW LOGISTICS INC.
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Message frequency varies based on your account activity</li>
                <li>Message and data rates may apply</li>
                <li>You can reply STOP to unsubscribe at any time</li>
                <li>Reply HELP for assistance</li>
            </ul>
            <p className="text-gray-700 mb-6">
                <strong>Important:</strong> If you opt out of SMS notifications, we will not send you any further text messages,
                including important service alerts or security notifications.
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mb-3">6. Data Security</h2>
            <p className="text-gray-700 mb-4">We implement appropriate security measures to protect your personal information, including:</p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                <li>Secure servers and encryption technologies</li>
                <li>Access controls and authentication protocols</li>
                <li>Regular security assessments and updates</li>
                <li>Breach notification procedures</li>
                <li>International data transfer safeguards</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-800 mb-3">7. Marketing Communications</h2>
            <p className="text-gray-700 mb-4">
                We may use your personal information to send you marketing communications about our services.
                You can opt out of these communications at any time by:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
                <li>Clicking the unsubscribe link in our emails</li>
                <li>Contacting us directly at the information provided below</li>
                <li>Updating your preferences in your account settings</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-800 mb-3">8. Your Rights</h2>
            <p className="text-gray-700 mb-4">You have the right to:</p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                <li>Access, correct, or update your personal information</li>
                <li>Withdraw consent for data processing</li>
                <li>Request data portability</li>
                <li>Request deletion of your personal information</li>
                <li>File complaints with data protection authorities</li>
                <li>Opt out of marketing communications</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-800 mb-3">9. Cookies and Tracking</h2>
            <p className="text-gray-700 mb-4">
                We use cookies and similar tracking technologies to enhance your experience on our website.
                You can control cookie settings through your browser preferences.
            </p>
            <p className="text-gray-700 mb-6">
                <strong>Note:</strong> Disabling cookies may limit some website functionality.
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mb-3">10. International Data Transfers</h2>
            <p className="text-gray-700 mb-6">
                Your information may be transferred to and processed in countries other than your own.
                We ensure appropriate safeguards are in place to protect your data during such transfers.
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mb-3">11. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 mb-6">
                We may update this Privacy Policy from time to time. We will notify you of any significant changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mb-3">12. Contact Information</h2>
            <p className="text-gray-700 mb-4">For questions about this Privacy Policy or our data practices, contact us at:</p>
            <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                    <strong>WILLOW LOGISTICS INC</strong><br />
                    254 Chapman Road, Suite 208<br />
                    Newark, DE 19702<br />
                    United States<br />
                    <br />
                    <strong>Email:</strong> <a href="mailto:anthony@willow-technologies.com" className="text-green hover:text-green-600 underline">anthony@willow-technologies.com</a><br />
                    <strong>Phone:</strong> <a href="tel:+16233028757" className="text-green hover:text-green-600 underline">+1 (623) 302-8757</a><br />
                    <strong>Website:</strong> <a href="https://willow-technologies.com/" target="_blank" rel="noopener noreferrer" className="text-green hover:text-green-600 underline">https://willow-technologies.com/</a>
                </p>
            </div>
        </div>
    );
};

export default PrivacyPolicy;